import { useParams, Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { categories, categoryMap } from '@/mocks/categories';
import { getArticlesByCategory } from '@/mocks/articles';
import ArticleCard from '@/components/feature/ArticleCard';
import Breadcrumb from '@/components/feature/Breadcrumb';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug || 'safe-access');

  useEffect(() => {
    if (slug) {
      setActiveSlug(slug);
      window.scrollTo({ top: 0, behavior: 'instant' });
    }
  }, [slug]);

  const category = categoryMap[activeSlug];
  const articles = getArticlesByCategory(activeSlug);

  if (!category) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-foreground-600">分类未找到</p>
      </div>
    );
  }

  const categoryName = category.name;

  const webpageSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${categoryName} - 成人网站安全指南`,
    description: category.description,
    url: `https://safe-guide.example.com/category/${activeSlug}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '安全指南',
      url: 'https://safe-guide.example.com/',
    },
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(webpageSchema) }}
      />
      <main className="min-h-screen pt-16">
        <Breadcrumb
          items={[
            { label: '首页', href: '/' },
            { label: categoryName },
          ]}
        />

        <section className="w-full px-4 md:px-6 py-6 md:py-8 bg-background-100 border-b border-background-200/70">
          <div className="max-w-6xl mx-auto">
            <h1 className="text-2xl md:text-3xl font-bold text-foreground-900 mb-2 font-heading">
              {categoryName}
            </h1>
            <p className="text-sm md:text-base text-foreground-600">
              {category.description}
            </p>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-4 md:py-6 border-b border-background-200/70 overflow-x-auto">
          <div className="max-w-6xl mx-auto">
            <div className="flex items-center gap-2 flex-nowrap min-w-max">
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.slug}`}
                  className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 cursor-pointer ${
                    activeSlug === cat.slug
                      ? 'bg-primary-500 text-white'
                      : 'bg-background-50 text-foreground-600 hover:bg-background-200 border border-background-200/70'
                  }`}
                >
                  <i className={`${cat.icon} mr-1.5`}></i>
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-8 md:py-12">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {articles.map((article) => (
                <ArticleCard key={article.id} article={article} />
              ))}
            </div>

            {articles.length === 0 && (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-200 text-foreground-400">
                  <i className="ri-article-line text-2xl"></i>
                </div>
                <p className="text-foreground-500 text-sm">该分类暂无文章</p>
              </div>
            )}
          </div>
        </section>
      </main>
    </>
  );
}